import React, { useState, useEffect } from "react";
import { Database, LogOut, CheckCircle, AlertCircle, RefreshCw, Archive, FileSpreadsheet, Download, CloudDownload } from "lucide-react";
import { initWorkspaceAuth, signInWithGoogleWorkspace, logoutWorkspace, uploadFileToDrive, listBackupFilesFromDrive, downloadFileFromDrive } from "../lib/firebase";
import { User } from "firebase/auth";

interface DataBackupPanelProps {
  apiToken: string;
}

export default function DataBackupPanel({ apiToken }: DataBackupPanelProps) {
  const [googleUser, setGoogleUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState<string | null>(null);
  const [spreadsheetId, setSpreadsheetId] = useState(localStorage.getItem('SPREADSHEET_ID') || '');

  // EXPERT COMMENTARY: React states tracking synchronization times and status of live database links.
  const [syncStatus, setSyncStatus] = useState<{ lastLocalBackup: string; lastFirestoreSync: string; firestoreAvailable: boolean } | null>(null);
  const [isSystemSyncing, setIsSystemSyncing] = useState(false);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);

  // Add state for listing backup files on Google Drive
  const [driveFiles, setDriveFiles] = useState<{ id: string; name: string; createdTime?: string }[]>([]);
  const [isListingFiles, setIsListingFiles] = useState(false);
  const [importingFileId, setImportingFileId] = useState<string | null>(null);

  // ADDITIVE: Lifetime Archives states
  const [lifetimeArchives, setLifetimeArchives] = useState<{ id: string; name: string; source: string; timestamp: number; size?: number; patientsCount?: number; createdAt: string }[]>([]);
  const [isListingLifetime, setIsListingLifetime] = useState(false);
  const [restoringLifetimeId, setRestoringLifetimeId] = useState<string | null>(null);
  const [isSavingManual, setIsSavingManual] = useState(false);

  // EXPERT COMMENTARY: Queries the visual sync monitor endpoint to fetch precise last-sync metadata.
  const fetchSyncStatus = async () => {
    setIsLoadingStatus(true);
    try {
      const res = await fetch("/api/admin/sync-status", {
        headers: {
          "Authorization": `Bearer ${apiToken}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setSyncStatus({
            lastLocalBackup: data.lastLocalBackup,
            lastFirestoreSync: data.lastFirestoreSync,
            firestoreAvailable: data.firestoreAvailable
          });
        }
      }
    } catch (err) {
      console.error("[Sync Status Fetch Failed]", err);
    } finally {
      setIsLoadingStatus(false);
    }
  };

  // EXPERT COMMENTARY: Triggers full local disk and Google Cloud Firestore synchronization on-demand.
  const triggerSystemSync = async () => {
    setIsSystemSyncing(true);
    try {
      const res = await fetch("/api/admin/sync-trigger", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${apiToken}`,
          "Content-Type": "application/json"
        }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSyncStatus({
          lastLocalBackup: data.lastLocalBackup,
          lastFirestoreSync: data.lastFirestoreSync,
          firestoreAvailable: data.firestoreAvailable
        });
        triggerToast("✓ تم إرسال ومزامنة السجلات الطبية السحابية والمحلية بنجاح!");
        fetchLifetimeArchives();
      } else {
        alert(`فشلت المزامنة اليدوية: ${data.error || "خطأ غير معروف"}`);
      }
    } catch (err: any) {
      console.error("[Sync Trigger Failed]", err);
      alert(`عذراً، فشل المزامنة: ${err.message}`);
    } finally {
      setIsSystemSyncing(false);
    }
  };

  const fetchLifetimeArchives = async () => {
    setIsListingLifetime(true);
    try {
      const res = await fetch("/api/admin/lifetime-archives", {
        headers: {
          "Authorization": `Bearer ${apiToken}`
        }
      });
      if (res.ok) {
        const result = await res.json();
        if (result.success && result.archives) {
          setLifetimeArchives(result.archives);
        }
      }
    } catch (err) {
      console.error("[Lifetime Lookup Failed]", err);
    } finally {
      setIsListingLifetime(false);
    }
  };

  const handleManualLifetimeSnapshot = async () => {
    setIsSavingManual(true);
    try {
      const res = await fetch("/api/admin/lifetime-archives/manual-save", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${apiToken}`,
          "Content-Type": "application/json"
        }
      });
      if (res.ok) {
        triggerToast("✓ تم حفظ لقطة الحفظ الرمزية والكاملة في الخادم المحلي وسحابة Google Cloud بنجاح تام!");
        fetchLifetimeArchives();
      } else {
        const errorData = await res.json();
        alert(`عذراً، فشل الحفظ الاحتياطي الفوري: ${errorData.error || "خطأ غير معروف"}`);
      }
    } catch (err: any) {
      alert(`خطأ في تهيئة اللقطة: ${err.message}`);
    } finally {
      setIsSavingManual(false);
    }
  };

  const handleRestoreLifetimeArchive = async (archive: any) => {
    const isConfirmed = window.confirm(
      `🔒 تنبيه أمني عالي الحساسية للعيادة والمريض!\n\nهل أنت متأكد من رغبتك في استرداد وتطبيق هذه النسخة السريرية من (${archive.source})؟\nالاسم: ${archive.name}\n\nسيقوم النظام باستبدال الحالة الحالية للعيادة والملفات بالكامل بهذه النسخة الاحتياطية بصفة فورية.`
    );
    if (!isConfirmed) return;

    setRestoringLifetimeId(archive.id);
    try {
      const res = await fetch("/api/admin/lifetime-archives/restore", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${apiToken}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          source: archive.source,
          id: archive.id
        })
      });
      
      const result = await res.json();
      if (res.ok && result.success) {
        triggerToast("✓ تم استرداد قاعدة البيانات وسجلات المرضى بالكامل بنجاح!");
        setTimeout(() => {
          if (window.confirm("تم استرداد البيانات ومزامنتها بنجاح مع الخادم. هل ترغب في تحديث جدول الطبيب ومخططات العيادة الآن؟")) {
            window.location.reload();
          }
        }, 800);
      } else {
        alert(`فشل الاسترداد: ${result.error || "تأكد من صلاحيات حساب الطبيب"}`);
      }
    } catch (err: any) {
      alert(`عذراً، حدث خطأ أثناء تواصل الاستيراد: ${err.message}`);
    } finally {
      setRestoringLifetimeId(null);
    }
  };
  
  // Helper to fetch files from Drive
  const fetchDriveBackups = async (tokenStr?: string) => {
    const activeToken = tokenStr || accessToken;
    if (!activeToken) return;
    setIsListingFiles(true);
    try {
      const result = await listBackupFilesFromDrive(activeToken);
      if (result.success && result.files) {
        setDriveFiles(result.files);
      } else {
        console.error("[Drive List Fail] Result unsuccessful:", result.error);
        if (result.error && (result.error.includes("invalid authentication credentials") || result.error.includes("expired") || result.error.includes("401"))) { setAccessToken(null); setIsAuthenticated(false); try { localStorage.removeItem("clinic_workspace_access_token"); } catch(e) {} }
      }
    } catch (err: any) {
      console.error("[Drive List Exception]", err);
    } finally {
      setIsListingFiles(false);
    }
  };

  useEffect(() => {
    fetchLifetimeArchives();
    fetchSyncStatus();
    const unsubscribe = initWorkspaceAuth(
      (user, token) => {
        setGoogleUser(user);
        setAccessToken(token);
        setIsAuthenticated(true);
        // Auto fetch the files on connection
        fetchDriveBackups(token);
      },
      () => {
        setGoogleUser(null);
        setAccessToken(null);
        setIsAuthenticated(false);
        setDriveFiles([]);
      }
    );
    return () => unsubscribe();
  }, []);

  const handleConnect = async () => {
    setIsLoggingIn(true);
    try {
      const result = await signInWithGoogleWorkspace();
      if (result) {
        setGoogleUser(result.user);
        setAccessToken(result.accessToken);
        setIsAuthenticated(true);
        triggerToast("تم ربط حساب Google بنجاح للنسخ الاحتياطي!");
        fetchDriveBackups(result.accessToken);
      }
    } catch (err: any) {
      console.error(err);
      alert(`خطأ أثناء عملية المصادقة: ${err.message}`);
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleDisconnect = async () => {
    if (window.confirm("هل أنت متأكد من رغبتك في إلغاء ربط حساب Google للتخزين السحابي؟")) {
      await logoutWorkspace();
      setGoogleUser(null);
      setAccessToken(null);
      setIsAuthenticated(false);
      setDriveFiles([]);
      triggerToast("تم إلغاء ربط الحساب بنجاح.");
    }
  };

  const triggerToast = (msg: string) => {
    setShowSuccessToast(msg);
    setTimeout(() => setShowSuccessToast(null), 5000);
  };

  const executeBackup = async (retryCount = 0) => {
    let currentToken = accessToken;
    if (!currentToken) {
      try {
        const result = await signInWithGoogleWorkspace();
        if (result) {
          setGoogleUser(result.user);
          setAccessToken(result.accessToken);
          setIsAuthenticated(true);
          currentToken = result.accessToken;
        } else return;
      } catch (e) {
        alert("فشل تجديد الاتصال بجوجل تلقائياً. يرجى الضغط على زر الربط.");
        return;
      }
    }

    setIsSyncing(true);
    try {
      const backupData = {
        timestamp: new Date().toISOString(),
        data: {} as any
      };
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) backupData.data[key] = localStorage.getItem(key);
      }
      
      const result = await uploadFileToDrive(
        currentToken, 
        `clinic-data-backup-${new Date().toISOString().replace(/:/g, '-')}.json`, 
        JSON.stringify(backupData, null, 2)
      );

      if (result.success) {
          triggerToast("تم النسخ الاحتياطي للبيانات بنجاح إلى Google Drive.");
          fetchDriveBackups(currentToken); // Refresh list immediately to show the newly added file
      } else {
          throw new Error(result.error || "فشل الرفع");
      }
    } catch (err: any) {
      if (err.message && (err.message.includes("invalid authentication credentials") || err.message.includes("expired") || err.message.includes("401"))) { 
        setAccessToken(null); setIsAuthenticated(false); try { localStorage.removeItem("clinic_workspace_access_token"); } catch(e) {} 
        if (retryCount === 0) {
          triggerToast("جاري تجديد جلسة المزامنة السحابية تلقائياً...");
          try {
            const res = await signInWithGoogleWorkspace();
            if (res) {
              setGoogleUser(res.user);
              setAccessToken(res.accessToken);
              setIsAuthenticated(true);
              setTimeout(() => executeBackup(1), 500);
            }
          } catch (retryErr) {
            alert("انتهت صلاحية الجلسة بالكامل، يرجى إعادة تسجيل الدخول للنسخ الاحتياطي.");
          }
        } else {
          alert("انتهت صلاحية جلسة Google، يرجى إعادة تسجيل الدخول للنسخ الاحتياطي.");
        }
      } else { 
        alert(`خطأ في النسخ الاحتياطي: ${err.message}`); 
      }
    } finally {
      setIsSyncing(false);
    }
  };

  const importBackupFromDrive = async (fileId: string, fileName: string, retryCount = 0) => {
    let currentToken = accessToken;
    if (!currentToken) {
      try {
        const result = await signInWithGoogleWorkspace();
        if (result) {
          setGoogleUser(result.user);
          setAccessToken(result.accessToken);
          setIsAuthenticated(true);
          currentToken = result.accessToken;
        } else return;
      } catch (e) {
        alert("فشل تجديد الاتصال. يرجى الضغط على زر الربط.");
        return;
      }
    }
    
    if (retryCount === 0) {
      // Clinical security: Explicit confirmation dialogue for destructive operations
      const confirmed = window.confirm(
        `تنبيه هام جداً! هل أنت متأكد من رغبتك في استيراد النسخة الاحتياطية (${fileName})؟\n\nسيؤدي هذا الإجراء إلى حذف واستبدال كافة سجلات المرضى والبيانات الحالية للعيادة بالبيانات المخزنة في هذه النسخة الاحتياطية.`
      );
      if (!confirmed) return;
    }
    
    setImportingFileId(fileId);
    try {
      const result = await downloadFileFromDrive(currentToken, fileId);
      if (result.success && result.content) {
        // Robust Error Handling & clinical validation check
        const parsed = JSON.parse(result.content);
        if (parsed && typeof parsed === "object" && parsed.data) {
          // Confirm structure is valid before writing to active state to preserve clinical integrity
          let keysCount = 0;
          Object.keys(parsed.data).forEach((key) => {
            const val = parsed.data[key];
            if (val !== null && val !== undefined) {
              localStorage.setItem(key, val);
              keysCount++;
            }
          });

          triggerToast(`✓ تم استرداد البيانات ومزامنتها بنجاح من Google Drive! (تم استيراد ${keysCount} سجل)`);
          
          // Ask if clinician wants to reload page right now to update all dashboard elements
          setTimeout(() => {
            if (window.confirm("تم استرداد البيانات القديمة بالكامل. هل ترغب في إعادة تحميل التطبيق الآن لتحديث الجداول وإحصائيات العيادة وتطبيق البيانات فوراً؟")) {
              window.location.reload();
            }
          }, 800);
        } else {
          throw new Error("ملف النسخة الاحتياطية غير متوافق أو لا يحتوي على كتل بيانات صالحة للعيادة.");
        }
      } else {
        throw new Error(result.error || "تعذر تحميل محتويات الملف.");
      }
    } catch (err: any) {
      console.error("[Restore Backup Failed]", err);
      if (err.message && (err.message.includes("invalid authentication credentials") || err.message.includes("expired") || err.message.includes("401"))) { 
        setAccessToken(null); setIsAuthenticated(false); try { localStorage.removeItem("clinic_workspace_access_token"); } catch(e) {} 
        if (retryCount === 0) {
          triggerToast("جاري تجديد الجلسة السحابية تلقائياً للاسترداد...");
          try {
            const res = await signInWithGoogleWorkspace();
            if (res) {
              setGoogleUser(res.user);
              setAccessToken(res.accessToken);
              setIsAuthenticated(true);
              setTimeout(() => importBackupFromDrive(fileId, fileName, 1), 500);
            }
          } catch (retryErr) {
            alert("انتهت صلاحية جلسة Google بالكامل، يرجى إعادة تسجيل الدخول للاسترداد.");
          }
        } else {
          alert("انتهت صلاحية جلسة Google، يرجى إعادة تسجيل الدخول.");
        }
      } else { 
        alert(`فشل استرداد النسخة الاحتياطية: ${err.message}`); 
      }
    } finally {
      setImportingFileId(null);
    }
  };

  return (
    <div className="bg-white border text-right border-[#E2E8F0] rounded-xl overflow-hidden shadow-sm transition-all duration-300">
      <div className="bg-slate-50 border-b border-[#E2E8F0] px-5 py-3.5 flex justify-between items-center cursor-pointer hover:bg-slate-100/75 select-none transition">
        <div className="flex items-center gap-2">
          <Database className="w-4 h-4 text-emerald-600" />
          <h3 className="font-bold text-[#1E293B] text-xs uppercase tracking-wide">
            التخزين السحابي Google Drive Backup
          </h3>
        </div>
        <div className="flex items-center gap-2">
          {isAuthenticated ? (
            <span className="bg-emerald-50 text-emerald-700 text-[8px] font-bold px-2 py-0.5 rounded-full border border-emerald-200">متصل</span>
          ) : (
            <span className="bg-slate-100 text-slate-500 text-[8px] font-bold px-2 py-0.5 rounded-full border border-slate-200">غير متصل</span>
          )}
        </div>
      </div>

      {/* EXPERT COMMENTARY: Visual 'Sync Status' monitor displaying live, dynamic timestamps of database replication.
          Incorporates safe fallback layout, custom loading cycles, and manual synchronization trigger buttons. */}
      <div className="bg-slate-900 border-b border-slate-950 p-5 text-slate-100">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 pb-3 border-b border-slate-800">
          <div>
            <h4 className="text-xs font-black text-indigo-400 uppercase tracking-widest flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              مراقب مزامنة قاعدة البيانات (Data Sync Monitor)
            </h4>
            <p className="text-[9px] text-slate-400 mt-1">تتبع وحفظ البيانات الطبية السريرية لضمان بقائها بنسخها المتعددة.</p>
          </div>
          <button
            type="button"
            onClick={triggerSystemSync}
            disabled={isSystemSyncing || isLoadingStatus}
            className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-500 disabled:bg-indigo-900 disabled:opacity-40 text-white font-extrabold text-[11px] px-3.5 py-2 rounded-lg transition active:scale-95 flex items-center justify-center gap-1.5 cursor-pointer shadow border border-indigo-500/30"
          >
            <RefreshCw className={`w-3 h-3 ${isSystemSyncing ? 'animate-spin' : ''}`} />
            <span>{isSystemSyncing ? "جاري الحفظ والمزامنة..." : "مزامنة يدوية فورية"}</span>
          </button>
        </div>

        <div className="grid grid-cols-1 gap-3.5 pt-3.5">
          {/* Cloud Synchronization Station */}
          <div className="bg-slate-950/45 border border-slate-800/60 rounded-xl p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-slate-800/70 border border-slate-700/60 flex items-center justify-center text-emerald-450 text-emerald-400">
              <RefreshCw className="w-4.5 h-4.5" />
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-[9px] uppercase font-black text-slate-400 block tracking-wider">☁️ سحابة المزامنة (Google Firestore)</span>
              <span className="text-[11px] font-mono font-bold text-slate-200 block truncate mt-0.5" dir="ltr">
                {syncStatus?.lastFirestoreSync ? new Date(syncStatus.lastFirestoreSync).toLocaleString("ar-IQ", { hour12: true }) : "جاري فحص التاريخ..."}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {showSuccessToast && (
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-[11px] p-3 rounded-lg flex items-center gap-1.5 animate-in fade-in duration-300">
            <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
            <span>{showSuccessToast}</span>
          </div>
        )}

        {!isAuthenticated ? (
          <div className="space-y-3.5 text-center py-2">
            <div className="bg-indigo-50/50 rounded-lg p-3 border border-indigo-100 max-w-sm mx-auto">
              <p className="text-[#475569] text-[11px] leading-relaxed">
                اربط حساب Google Drive لتمكين النسخ الاحتياطي الشامل للعيادة. يتم حفظ البيانات كملفات مضغوطة وجداول بيانات.
              </p>
            </div>
            <button
              type="button"
              onClick={handleConnect}
              disabled={isLoggingIn}
              className="gsi-material-button w-full flex items-center justify-center gap-2.5 bg-white border border-[#DADCE0] hover:bg-[#F8FAFC] active:bg-[#F1F3F4] text-[#3C4043] font-bold text-xs py-2.5 px-4 rounded-md shadow-sm transition hover:scale-[101%] duration-200 cursor-pointer"
            >
              {!isLoggingIn ? (
                <>
                    <svg className="w-4 h-4" viewBox="0 0 48 48">
                      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
                      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
                      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
                      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
                    </svg>
                  <span>ربط حساب للنسخ السحابي</span>
                </>
              ) : (
                <div className="flex items-center gap-2">
                  <RefreshCw className="w-3.5 h-3.5 animate-spin text-indigo-600" />
                  <span>جاري الاتصال...</span>
                </div>
              )}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-slate-50 border border-[#E2E8F0] rounded-lg p-3 flex items-center justify-between text-right">
              <div className="flex items-center gap-2.5">
                {googleUser?.photoURL ? (
                  <img src={googleUser.photoURL} alt="Google account avatar" className="w-8 h-8 rounded-full border border-indigo-200" referrerPolicy="no-referrer" />
                ) : (
                  <div className="w-8 h-8 bg-emerald-600 text-white rounded-full flex items-center justify-center font-bold text-xs">
                    {googleUser?.displayName?.charAt(0) || "G"}
                  </div>
                )}
                <div>
                  <h5 className="font-bold text-xs text-slate-800 leading-none">{googleUser?.displayName || "مستخدم موثق"}</h5>
                  <p className="text-[10px] text-slate-500 font-mono mt-0.5">{googleUser?.email}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleDisconnect}
                className="p-1 px-2.5 text-rose-600 hover:text-white hover:bg-rose-500 rounded border border-rose-200 hover:border-rose-500 text-[10px] font-bold tracking-wide transition flex items-center gap-1 cursor-pointer"
              >
                <LogOut className="w-3 h-3" />
                إلغاء الربط
              </button>
            </div>

            <div className="grid grid-cols-1 pt-2">
              <button
                type="button"
                onClick={executeBackup}
                disabled={isSyncing}
                className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white font-bold text-[12px] py-3 rounded-lg shadow-sm transition active:scale-95 cursor-pointer disabled:cursor-wait"
              >
                {isSyncing ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>جاري التصدير...</span>
                  </>
                ) : (
                  <>
                    <Archive className="w-4 h-4" />
                    <span>نسخ احتياطي للبيانات</span>
                  </>
                )}
              </button>
            </div>

            {/* Google Drive Backups List with Direct Import Icon (Additive Feature) */}
            <div className="bg-slate-50 border border-slate-200/80 rounded-lg p-3.5 space-y-3 mt-3">
              <div className="flex justify-between items-center pb-2 border-b border-slate-200">
                <span className="text-[10.5px] font-black text-slate-700 uppercase tracking-wide">
                  النسخ الاحتياطية المتوفرة على السحابة (Google Drive)
                </span>
                <button
                  type="button"
                  onClick={() => fetchDriveBackups()}
                  disabled={isListingFiles}
                  className="p-1 text-slate-500 hover:text-slate-800 hover:bg-slate-200/60 rounded transition cursor-pointer"
                  title="تحديث قائمة الملفات"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${isListingFiles ? 'animate-spin' : ''}`} />
                </button>
              </div>

              {isListingFiles ? (
                <div className="flex items-center justify-center gap-2 py-4 text-xs font-semibold text-slate-500">
                  <RefreshCw className="w-4 h-4 animate-spin text-emerald-600" />
                  <span>جاري جلب ملفات النسخ من Drive...</span>
                </div>
              ) : driveFiles.length > 0 ? (
                <div className="max-h-40 overflow-y-auto space-y-1.5 pr-1 divide-y divide-slate-100">
                  {driveFiles.map((file) => (
                    <div key={file.id} className="flex items-center justify-between text-right py-2 first:pt-0 last:pb-0 gap-2">
                      <div className="space-y-0.5 min-w-0">
                        <div className="text-xs font-black text-slate-800 truncate" title={file.name}>
                          {file.name}
                        </div>
                        {file.createdTime && (
                          <div className="text-[9px] text-slate-400 font-bold">
                            تاريخ الرفع: {new Date(file.createdTime).toLocaleString("ar-EG")}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => importBackupFromDrive(file.id, file.name)}
                        disabled={importingFileId !== null}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] bg-indigo-50 hover:bg-indigo-100 text-indigo-700 hover:text-indigo-800 border border-indigo-200/60 rounded-md font-bold transition active:scale-[98%] cursor-pointer disabled:opacity-50 select-none"
                        title="استيراد هذه النسخة واسترجاع البيانات"
                      >
                        {importingFileId === file.id ? (
                          <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <CloudDownload className="w-3.5 h-3.5" />
                        )}
                        <span>استيراد</span>
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-[10px] text-slate-400 font-bold leading-normal">
                  لا توجد سجلات نسخ احتياطي (clinic-data-backup-*.json) على حساب Google Drive هذا حتى الآن.
                </div>
              )}
            </div>

            {/* ADDITIVE: Lifetime Clinical Restoration & Recovery Archives Vault */}
            <div className="bg-slate-900 text-slate-100 border border-slate-950 rounded-lg p-4 space-y-3.5 mt-3 shadow-md">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 pb-2.5 border-b border-slate-755 border-slate-800/80">
                <div>
                  <h4 className="text-[11px] font-black text-indigo-400 uppercase tracking-wide flex items-center gap-1.5">
                    🛡️ مستودع الضمان والأرشيف الدائم للعيادة مدى الحياة
                  </h4>
                  <p className="text-[9px] text-slate-400 mt-0.5">حماية تلقائية لجميع سجلات المرضى والملفات السريرية بدون حذف نهائياً</p>
                </div>
                <div className="flex items-center gap-1.5 self-end sm:self-center">
                  <button
                    type="button"
                    onClick={fetchLifetimeArchives}
                    disabled={isListingLifetime}
                    className="p-1 px-1.5 text-slate-300 hover:text-white hover:bg-slate-800 rounded text-[10px] transition font-bold"
                    title="مزامنة أرشيف الوقت الفعلي"
                  >
                    {isListingLifetime ? <RefreshCw className="w-3 h-3 animate-spin text-teal-400" /> : "تحديث القائمة"}
                  </button>
                  <button
                    type="button"
                    onClick={handleManualLifetimeSnapshot}
                    disabled={isSavingManual}
                    className="bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 px-2.5 py-1 text-xs text-white rounded font-extrabold flex items-center gap-1 text-[9.5px]"
                  >
                    {isSavingManual ? <RefreshCw className="w-3 h-3 animate-spin" /> : "لقطة حفظ فوري"}
                  </button>
                </div>
              </div>

              {isListingLifetime ? (
                <div className="flex items-center justify-center gap-2 py-5 text-[10.5px] text-slate-400 font-bold">
                  <RefreshCw className="w-3.5 h-3.5 animate-spin text-indigo-400" />
                  <span>جاري قراءة سجلات الاسترجاع من خادم العيادة و Google Cloud...</span>
                </div>
              ) : lifetimeArchives.length > 0 ? (
                <div className="max-h-52 overflow-y-auto space-y-2 pr-1 divide-y divide-slate-800/60">
                  {lifetimeArchives.map((archive, index) => {
                    const isRestoring = restoringLifetimeId === archive.id;
                    const dateString = new Date(archive.timestamp).toLocaleString("ar-IQ", {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                      hour: "numeric",
                      minute: "2-digit",
                      second: "2-digit"
                    });
                    const isLocal = archive.source === "Local Server";

                    return (
                      <div key={archive.id + "_" + index} className="flex items-center justify-between text-right py-2.5 first:pt-0 last:pb-0 gap-2">
                        <div className="space-y-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className={`text-[8.5px] px-1.5 py-0.5 rounded font-black ${isLocal ? "bg-slate-800 text-slate-300 border border-slate-700" : "bg-teal-950/40 text-teal-400 border border-teal-900/35"}`}>
                              {isLocal ? "🖥️ خادم محلي" : "☁️ Google Cloud"}
                            </span>
                            <span className="text-[10.5px] font-black text-slate-200 truncate">
                              {archive.name}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-[9px] text-slate-400 font-bold">
                            <span>الوقت: {dateString}</span>
                            {archive.patientsCount !== undefined && (
                              <>
                                <span>•</span>
                                <span className="text-teal-300">المرضى: {archive.patientsCount}</span>
                              </>
                            )}
                            {archive.size ? (
                              <>
                                <span>•</span>
                                <span>الحجم: {(archive.size / 1024).toFixed(1)} KB</span>
                              </>
                            ) : null}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRestoreLifetimeArchive(archive)}
                          disabled={restoringLifetimeId !== null}
                          className="flex items-center gap-1 px-2.5 py-1 text-[9.5px] bg-slate-800 hover:bg-slate-700 active:scale-95 text-slate-100 rounded border border-slate-700 font-extrabold transition select-none disabled:opacity-40"
                          title="استرداد كامل محتويات هذا التاريخ التراكمي"
                        >
                          {isRestoring ? (
                            <RefreshCw className="w-3 h-3 animate-spin text-teal-400" />
                          ) : (
                            "استرجاع"
                          )}
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="bg-slate-950/40 p-4 rounded border border-dashed border-slate-800 text-center py-5 text-[10px] text-slate-400 font-bold">
                  بانتظار بناء سجلات الأرشيف الأولى. يقوم النظام بمزامنة لقطة أوتوماتيكية لأقسام العيادة مع كل حركة أو تسجيل جديد.
                </div>
              )}
            </div>

            <div className="bg-slate-50 border border-[#E2E8F0] rounded-lg p-3 mt-3">
              <label className="block text-[10px] text-slate-500 font-bold mb-1.5 uppercase tracking-wide">معرف جدول بيانات Google (Spreadsheet ID)</label>
              <input
                type="text"
                value={spreadsheetId}
                onChange={(e) => setSpreadsheetId(e.target.value)}
                className="w-full text-xs p-2 rounded border border-slate-300 focus:ring-1 focus:ring-emerald-500 outline-none"
                placeholder="أدخل ID جدول البيانات..."
              />
              <button type="button" onClick={() => { localStorage.setItem('SPREADSHEET_ID', spreadsheetId); triggerToast("تم حفظ المعرف!"); }} className="mt-2 w-full bg-slate-100 hover:bg-slate-200 text-slate-700 text-[10px] font-bold py-1.5 rounded">حفظ الإعدادات</button>
            </div>
            <div className="bg-sky-50 text-[10px] text-sky-800 p-3 rounded-lg border border-sky-100 mt-2 flex gap-2 w-full text-right leading-relaxed">
              <FileSpreadsheet className="w-6 h-6 text-sky-600 shrink-0 opacity-80" />
              <span>
                النسخ الاحتياطي للبيانات يرفع ملف الداتا JSON وجداول الـ Sheets. النسخ الاحتياطي للكود يرفع شفرة التطبيق وملفاته إلى Drive لحمايته من الفقدان.
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
